/**
 * Persists the converter UI across navigation until the user starts a new spec
 * (upload / URL / paste) or clears storage.
 */
import type { Credentials } from "../components/AuthConfigStep";
import type { OverrideMode } from "../components/AuthOverrideStep";

const STORAGE_KEY = "apitomcp.conversion-workspace.v1";

export type ConversionWorkspaceConnect = {
  specTitle: string;
  baseUrl: string;
  serverPath: string;
  mcpUrl: string;
  sseUrl: string;
};

export type ConversionWorkspaceV1 = {
  v: 1;
  updatedAt: string;
  parsedRawData: unknown | null;
  fetchedSpecUrl: string | null;
  pastedSpecText: string | null;
  fileSnapshot: { fileName: string; fileType: string; dataBase64: string } | null;
  baseUrlOverride: string;
  credentials: Credentials;
  credentialsSkipped?: boolean;
  authOverride: OverrideMode | null;
  hostedMcpDisplayUrl: string | null;
  connectSetup: ConversionWorkspaceConnect | null;
  lastConvertSuccess: boolean;
  serverId: string | null;
};

/** Workspace expires after 2 hours of inactivity */
const WORKSPACE_TTL_MS = 2 * 60 * 60 * 1000;

export function loadConversionWorkspace(): ConversionWorkspaceV1 | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as ConversionWorkspaceV1;
    if (data?.v !== 1) return null;
    // Discard stale workspaces so old specs don't appear on fresh page loads
    if (data.updatedAt) {
      const age = Date.now() - new Date(data.updatedAt).getTime();
      if (age > WORKSPACE_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
    }
    return data;
  } catch {
    return null;
  }
}

export function saveConversionWorkspace(patch: Partial<ConversionWorkspaceV1>): void {
  if (typeof window === "undefined") return;
  try {
    const prev = loadConversionWorkspace();
    const next: ConversionWorkspaceV1 = {
      v: 1,
      updatedAt: new Date().toISOString(),
      parsedRawData: prev?.parsedRawData ?? null,
      fetchedSpecUrl: prev?.fetchedSpecUrl ?? null,
      pastedSpecText: prev?.pastedSpecText ?? null,
      fileSnapshot: prev?.fileSnapshot ?? null,
      baseUrlOverride: prev?.baseUrlOverride ?? "",
      credentials: prev?.credentials ?? {},
      credentialsSkipped: prev?.credentialsSkipped ?? false,
      authOverride: prev?.authOverride ?? null,
      hostedMcpDisplayUrl: prev?.hostedMcpDisplayUrl ?? null,
      connectSetup: prev?.connectSetup ?? null,
      lastConvertSuccess: prev?.lastConvertSuccess ?? false,
      serverId: prev?.serverId ?? null,
      ...patch,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota / private mode */
  }
}

export function clearConversionWorkspace(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
